import { useEffect, useState } from 'react';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { MappedAEMProperties } from '../../../../types';
import Layout from '../../../common/Layout/Layout';
import styles from './RelatedVideos.module.scss';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { useRelatedVideosDetails } from '../../../../lib/api/videoApi';
import MicrolearningCard from '../../base/VideoCard/VideoCard';
import { PureSeparator } from '../Separator/Separator';
import Button from '../../../common/Button/Button';
import { RelatedVideosBrandSkeleton, RelatedVideosSkeleton } from './RelatedVideosSkeleton';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';
import { Label } from '../../../common/Label/Label';
import { Api } from '../../../../lib/types/apiSpec';
import { VIDEO_LABELS } from '../../../../lib/constants/labelKeys';
import { relatedVideosIds } from '../../../../lib/tests/testIds';
import { PageType } from '../../../../lib/hooks/usePageDetails';

export type RelatedVideosProps = {
  heading?: string;
  headingLevel?: HeadingLevel;
  icon?: string;
  loadMoreCount: number;
} & MappedAEMProperties;

const RelatedVideos = ({ heading, headingLevel, icon, loadMoreCount }: RelatedVideosProps) => {
  const { page, isLoading: isPageDetailsLoading } = useViewContext();

  const { video, error } = page;
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const {
    data,
    isLoading: isVideoDetailsLoading,
    isValidating: isValidatingVideoDetails,
    error: videoDetailsError,
  } = useRelatedVideosDetails({
    videoId: video?.id ?? '',
    pageSize: loadMoreCount,
    cursor,
    shouldFetch: !!video?.id,
  });
  const [videosToDisplay, setVideosToDisplay] = useState<(Api.MicrolearningDetails | Api.RelatedVideoDetails)[]>([]);
  const relatedVideosHeadingId = `${video?.id}-related-videos-heading`;

  useEffect(() => {
    setVideosToDisplay([]);
  }, [video?.id]);

  useEffect(() => {
    setVideosToDisplay(prev => [...prev, ...(data?.hits ?? [])]);
  }, [data]);

  if (data && videosToDisplay.length === 0) {
    return null;
  }

  return (
    <div className={styles.mainWrapper} aria-labelledby={relatedVideosHeadingId}>
      <Layout>
        {!!heading && (
          <HeadingWithIcon
            level={HeadingLevel.h2}
            as={headingLevel}
            icon={icon}
            classNames={{ icon: styles.headingIcon }}
            id={relatedVideosHeadingId}
          >
            {heading}
          </HeadingWithIcon>
        )}
        <PureSeparator className={styles.separator} />
        <div
          className={styles.mainGridWrapper}
          aria-live="polite"
          aria-atomic="true"
          aria-busy={isVideoDetailsLoading || isValidatingVideoDetails}
        >
          <ErrorBoundary error={error} className={styles.generalErrorBoundary} internal>
            <RelatedVideosBrandSkeleton isLoading={!!isPageDetailsLoading}>
              <p className={styles.brand} data-testid={relatedVideosIds.brand}>
                {video?.productBrands?.join(', ')}
              </p>
            </RelatedVideosBrandSkeleton>
            <div className={styles.videos}>
              <ErrorBoundary error={videoDetailsError} internal>
                <ol className={styles.videosGrid}>
                  <RelatedVideosSkeleton isLoading={isVideoDetailsLoading && !data?.hits?.length}>
                    {videosToDisplay.map(v => (
                      <li key={v.id}>
                        <MicrolearningCard
                          type={
                            page.type === PageType.VideoDetails
                              ? Api.CommonVideoType.Video
                              : Api.CommonVideoType.Microlearning
                          }
                          {...v}
                        />
                      </li>
                    ))}
                  </RelatedVideosSkeleton>
                </ol>
                {!!data?.hits?.length && !!data?.cursor && (
                  <>
                    <PureSeparator />
                    <Button
                      variant="outlined"
                      className={styles.showMoreButton}
                      onClick={() => setCursor(data?.cursor)}
                      isLoading={isVideoDetailsLoading}
                    >
                      <Label name={VIDEO_LABELS.RELATED_VIDEOS.SHOW_MORE} /> ({loadMoreCount})
                    </Button>
                  </>
                )}
              </ErrorBoundary>
            </div>
          </ErrorBoundary>
        </div>
      </Layout>
    </div>
  );
};

export default RelatedVideos;
